import React from 'react';
import { isBefore, isWeekend } from 'date-fns';
import { Typography, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import DatePicker from 'components/@common/Inputs/DatePicker';
import useLessonAssignment from 'state/providers/LessonAssignmentProvider';
import { HwtButton } from 'components/@common/Buttons/HwtButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faUpload } from '@fortawesome/free-solid-svg-icons';
import { remCalc } from 'util/style-util';
import FileUploadButton from 'components/FileUploadButton';
import { translate } from 'translations/translator';
import { FilesContent } from './FilesContent';
import { StepOneAssignmentAlerts } from './StepOneAssignmentAlerts';
import TextInput from 'components/@common/Inputs/TextInput';
import MicRecorderButton from 'components/AudioRecorder/MicRecorderButton';

const getTranslation = key => translate('lessonAssignment', key);

export const StepOneEditAssignmentHeader = ({
  lessonTitle,
  handleCollapse,
  duration,
  noActivities
}) => {
  const { flexItems, ...classes } = useStyles()();
  const [
    { lessonAssignmentState, stepOneFormState },
    { stepOneFormDispatch /*stepperDispatch,lessonAssignmentDispatch */ }
  ] = useLessonAssignment();

  const handleAudioInstructions = ({ target }) => {
    stepOneFormDispatch.onFormChange(target.name, [target.files[0]]);
  };

  const handleRecordsuccess = file => {
    stepOneFormDispatch.onFormChange('audioFiles', [file]);
  };

  const handleRemoveFile = name => () => {
    stepOneFormDispatch.onRemoveFileAdded(name);
  };

  const disableWeekends = date => isWeekend(date);

  const disableDatesBeforeStart = startDate => date =>
    isBefore(new Date(date), new Date(startDate)) || disableWeekends(date);

  return (
    <>
      <Box className={`${flexItems} ${classes.container}`}>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <TextInput
            size="small"
            label={getTranslation('assignmentTitle')}
            InputProps={{
              style: {
                width: remCalc(585),
                fontSize: remCalc(24),
                fontWeight: '400 !important',
                border: '2px solid'
              }
            }}
            error={stepOneFormState.errors?.[lessonAssignmentState.activeFormId]?.title}
            value={
              stepOneFormState.form?.[lessonAssignmentState.activeFormId]?.title ?? lessonTitle
            }
            name={'title'}
            onChange={stepOneFormDispatch.onFormChange}
            width={remCalc(585)}
          >
            {lessonTitle}
          </TextInput>
        </Box>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <HwtButton
            onClick={handleCollapse}
            style={
              {
                backgroundColor: 'transparent',
                width: 'auto',
                textTransform: 'none !important'
              } as any
            }
          >
            <FontAwesomeIcon icon={faArrowUp} fontSize="14" style={{ marginRight: 10 }} />
            {getTranslation('collapse')}
          </HwtButton>
        </Box>
      </Box>

      <Box className={`${flexItems} ${classes.picker}`}>
        <DatePicker
          label={getTranslation('assignmentStartDate')}
          sx={{
            borderRadius: '4px',
            border: '2px solid',
            width: '48%',
            marginTop: '20px',
            marginBottom: '20px'
          }}
          name={'startDate'}
          value={stepOneFormState.form?.[lessonAssignmentState.activeFormId]?.startDate ?? ''}
          onChange={stepOneFormDispatch.handleDateChange}
          shouldDisableWeekends
          // error={stepOneFormState.errors?.[lessonAssignmentState.activeFormId]?.startDate}
        />
        <DatePicker
          label={getTranslation('assignmentDueDate')}
          shouldDisableDate={disableDatesBeforeStart(
            stepOneFormState.form?.[lessonAssignmentState.activeFormId]?.startDate
          )}
          sx={{
            borderRadius: '4px',
            border: '2px solid',
            width: '48%',
            marginTop: '20px',
            marginBottom: '20px'
          }}
          name={'dueDate'}
          value={stepOneFormState.form?.[lessonAssignmentState.activeFormId]?.dueDate ?? ''}
          onChange={stepOneFormDispatch.handleDateChange}
          shouldDisableWeekends
          // error={stepOneFormState.errors?.[lessonAssignmentState.activeFormId]?.dueDate}
        />
      </Box>
      <Box className={`${flexItems} ${classes.picker}`}>
        <Box>
          <TextInput
            name={'instructions'}
            rows={3}
            label={getTranslation('teacherInstructions')}
            value={stepOneFormState.form?.[lessonAssignmentState.activeFormId]?.instructions ?? ''}
            onChange={stepOneFormDispatch.onFormChange}
            error={stepOneFormState.errors?.[lessonAssignmentState.activeFormId]?.instructions}
            size="medium"
            width={'470px'}
            sx={{
              '& .MuiInputBase-input': {
                height: '140px !important'
              }
            }}
            InputProps={{
              style: {
                height: '150px',
                border: '2px solid',
                width: '470px'
              }
            }}
            multiline
            placeholder={getTranslation('typeYourInstructionsHere')}
          />
        </Box>

        <Box
          style={{
            display: 'flex',
            flex: '.7 0 42%',
            flexDirection: 'column',
            justifyContent: 'space-evenly',
            alignSelf: 'baseline'
          }}
        >
          <Typography variant="subtitle2" style={{ marginBottom: '10px', marginTop: '8px' }}>
            {getTranslation('audioInstructions')}
          </Typography>
          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            <FilesContent handleRemove={handleRemoveFile} />
            <Box display="flex" flexDirection="row">
              <MicRecorderButton
                onSendMessage={handleRecordsuccess}
                maxRecordTime={120}
                teacherInstructions={true}
                buttonContent={
                  <HwtButton variant="contained" style={{ marginRight: '10px' }}>
                    {getTranslation('recordAudio')}
                  </HwtButton>
                }
              />

              <FileUploadButton
                btnClass={classes.fileUpload}
                variant="outlined"
                name="audioFiles"
                onChange={handleAudioInstructions}
                value={''}
              >
                <FontAwesomeIcon icon={faUpload} size="sm" style={{ margin: '0 5px' }} />
                {getTranslation('uploadFromDevice')}
              </FileUploadButton>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box display="flex">
        <StepOneAssignmentAlerts type={'duration'} duration={duration} />
      </Box>
      {noActivities && (
        <Box display="flex">
          <StepOneAssignmentAlerts type={'noActivities'} />
        </Box>
      )}
    </>
  );
};

const useStyles = () =>
  makeStyles(() => ({
    flexItems: {
      display: 'flex',
      flex: 1
    },
    picker: {
      justifyContent: 'space-between',
      padding: '0 32px',
      '& .MuiFormControl-marginNormal': {
        margin: '20px 0'
      }
    },
    container: {
      alignItems: 'center',
      padding: '32px 32px 0px 32px',
      justifyContent: 'space-between'
    },
    btn: {
      margin: '0px 10px'
    },
    fileUpload: {
      whiteSpace: 'nowrap'
    },
    disabled: {
      color: '#A6A6A6 !Important',
      '&:disabled': {
        border: 'none'
      }
    }
  }));
